import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Keyboard } from 'lucide-react';
import { CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useAppStore } from 'store';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { startReceipt } from 'services/api/receipt';
import { useAuthStore } from 'store/authStore';
import { toast } from 'sonner';
import { useArrivalReceiptStore } from 'store/arrivalReceiptStore';
import { getFedexId } from 'services/barcodes/checkFedexId';
import { playErrorSound, playGoodSound, playCallbackSound } from 'utils/sounds';
import { cn } from 'utils/classnames';
import { ReceivingCallbackScreen } from './ReceivingCallbackScreen';
import { ReceivingGradingScreen } from './ReceivingGradingScreen';
import { ArrivalDispositionSheet } from '../ArrivalDispositionSheet';
import { ProblemScreen } from '../ProblemScreen';

const isCallbackResult = (responseState: string, arrivalDisposition: string): boolean =>
  arrivalDisposition?.toLowerCase().includes('exception');

export default function Receiving() {
  const [trackingId, setTrackingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDisposition, setShowDisposition] = useState(false);
  const trackingInputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(trackingInputRef);
  const { stationId, user } = useAuthStore();
  const { currentArrivalReceipt, setCurrentArrivalReceipt } = useArrivalReceiptStore();
  const { setBarcodeDebugMessage } = useAppStore();

  useEffect(() => {
    setTrackingId('');
    setShowDisposition(false);
    requestAnimationFrame(() => trackingInputRef.current?.focus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const el = trackingInputRef.current;
      if (el && !el.disabled && document.activeElement !== el) el.focus();
    }, 200);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (): Promise<void> => {
    let trimmed = trackingId.trim();
    if (!trimmed) {
      toast.warning('Please enter tracking number');
      return;
    }

    const fedexId = getFedexId(trimmed);
    if (fedexId !== trimmed) {
      toast.success('✓ FedEx tracking detected! Processing...');
      setBarcodeDebugMessage(`FedEx ID detected: ${fedexId}`);
      trimmed = fedexId;
      setTrackingId(fedexId);
    }

    setLoading(true);
    try {
      const data = await startReceipt(trimmed, user?.id ?? '', stationId ?? '');
      if (!data || data.responseState === 'error') {
        playErrorSound();
        toast.error(data?.responseMsg ?? 'Request failed');
        setTrackingId('');
      } else {
        if (data.responseState === 'problem') {
          playErrorSound();
        } else if (isCallbackResult(data.responseState, data.arrival_disposition)) {
          playCallbackSound();
        } else {
          playGoodSound();
        }
        setTrackingId('');
        setCurrentArrivalReceipt(data);
        setShowDisposition(true);
      }
    } catch (err) {
      playErrorSound();
      toast.error((err as { message?: string }).message ?? 'Request failed');
      setTrackingId('');
    } finally {
      setLoading(false);
    }
  };

  const scanInput = (inResult: boolean) => (
    <div className="flex items-center gap-2.5">
      <Input
        ref={trackingInputRef}
        id="receiving-tracking"
        type="text"
        inputMode={keyboardVisible ? 'text' : 'none'}
        placeholder="Scan tracking..."
        value={trackingId}
        onChange={(e) => setTrackingId(e.target.value)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleSubmit();
          }
        }}
        onBlur={() => {
          if (!loading) requestAnimationFrame(() => trackingInputRef.current?.focus());
        }}
        disabled={loading}
        autoComplete="off"
        spellCheck={false}
        className={cn('text-base', inResult && 'bg-white/95 border-white/30')}
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className={cn(
          'h-12 w-12 shrink-0 rounded-xl',
          inResult ? 'bg-white/90 border-white/30' : 'border-slate-300'
        )}
        onClick={toggleKeyboardInput}
        aria-pressed={keyboardVisible}
      >
        <Keyboard className="h-5 w-5" />
      </Button>
    </div>
  );

  if (showDisposition && currentArrivalReceipt) {
    const isProblem =
      currentArrivalReceipt.responseState === 'problem' ||
      currentArrivalReceipt.responseState === 'exception';
    const isCallback = isCallbackResult(
      currentArrivalReceipt.responseState,
      currentArrivalReceipt.arrival_disposition
    );

    if (isProblem) {
      return <ProblemScreen result={currentArrivalReceipt} scanInput={scanInput(true)} />;
    }

    const dispositionSheet = <ArrivalDispositionSheet className="w-full rounded-2xl border mb-4" />;

    if (isCallback) {
      return (
        <ReceivingCallbackScreen
          result={currentArrivalReceipt}
          scanInput={scanInput(true)}
          dispositionSheet={dispositionSheet}
        />
      );
    }

    return (
      <ReceivingGradingScreen
        result={currentArrivalReceipt}
        scanInput={scanInput(true)}
        dispositionSheet={dispositionSheet}
      />
    );
  }

  return (
    <div className="space-y-4">
      <CardTitle className="mb-1 flex items-center justify-center gap-3 text-center text-xl font-extrabold tracking-tight text-slate-900">
        Scan Shipping Box
      </CardTitle>

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label
          htmlFor="receiving-tracking"
          className="mb-2 block text-sm font-semibold text-slate-700"
        >
          Tracking #
        </Label>
        {scanInput(false)}
      </div>
      <ArrivalDispositionSheet />
    </div>
  );
}
